import { appAlert, appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// ActionCentre.js
// Fleet-wide Action Centre — full-page view opened from the Head Office card.
//
// Three stacked sections:
//   1. Messages awaiting acknowledgement  (oldest first)
//        date · pharmacy · recipient · subject · content (hover → full popup) · status
//        Only messages sent with "Require acknowledgement" ticked, and not
//        yet acknowledged, appear here. The recipient normally acknowledges
//        from a button in the email itself (a public, no-login link — see
//        the backend's /action-centre/ack/:token routes); "Mark acknowledged"
//        here is a manual override for e.g. a phone confirmation.
//   2. Current possible messages to send
//        per-pharmacy suggestions with Include + Require-acknowledgement
//        checkboxes, "Add another message" per pharmacy, "Add message to all
//        pharmacies" at the top, a Preview per pharmacy, and a "Send message to
//        all pharmacies" button. On send: ack-required messages go to grid 1,
//        everything else goes straight to grid 3 — there's no reliable way to
//        detect a real email being "opened", so that's no longer tracked as a
//        gate on either grid.
//   3. Acknowledged / no-acknowledgement-required messages  (reverse chrono)
//        same columns as grid 1, with a search box (searches the whole entry)
//        and a pharmacy filter, loaded in pages.
//
// Near-miss alerts from /near_miss_alerts_fleet seed the per-pharmacy
// suggestions where available; the rest of the suggestion text is front-end
// mock / in-session state. Sending and acknowledgement tracking are real.

import React, { useEffect, useRef, useState, useMemo } from "react";
import { displayName, useMaskPharmacyNames, MaskToggleButton } from "./nameMask";
const API_BASE = apiUrl("");
const PAGE_SIZE = 15;

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
function fmtDateTime(d) {
  if (!d) return "—";
  const t = new Date(d);
  if (isNaN(t.getTime())) return "—";
  return t.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }) + " " + t.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit"
  });
}
let _uid = 0;
const nextId = () => `m${Date.now()}_${++_uid}`;
const normBranch = v => String(v || "").trim().toLowerCase();

// Organisation members are the authority for which branch cards may appear.
// members[].pharmacyId is the tenant dbName UUID in this application.
function organisationMemberIndex() {
  let members = [];
  try {
    members = JSON.parse(localStorage.getItem("members") || "[]");
  } catch {
    members = [];
  }
  const list = (Array.isArray(members) ? members : []).filter(Boolean).map(m => ({
    name: String(m.pharmacyName || m.name || m.pharmacyId || "").trim(),
    dbName: String(m.dbName || m.pharmacyId || "").trim(),
    raw: m
  })).filter(m => m.name);
  const byDbName = new Map(list.filter(m => m.dbName).map(m => [m.dbName, m]));
  const byName = new Map(list.map(m => [normBranch(m.name), m]));
  return {
    list,
    byDbName,
    byName
  };
}

/* ------------------------------------------------------------------ */
/* Shared: a read-only message grid (used by grids 1 and 3)           */
/* ------------------------------------------------------------------ */
function MessageGrid({
  rows,
  statusLabel,
  statusColor,
  oldestFirst,
  masked,
  onHoverContent
}) {
  const th = {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "#5D5D5D",
    borderBottom: "2px solid #F0F0F0",
    whiteSpace: "nowrap"
  };
  const td = {
    padding: "9px 12px",
    fontSize: 13,
    borderBottom: "1px solid #f0f1f3",
    verticalAlign: "top"
  };
  const ordered = [...rows].sort((a, b) => {
    const ta = new Date(a.sentAt || a.createdAt || 0).getTime();
    const tb = new Date(b.sentAt || b.createdAt || 0).getTime();
    return oldestFirst ? ta - tb : tb - ta;
  });
  return <div style={{
    overflowX: "auto",
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    background: "#fff"
  }}>
      <table style={{
      width: "100%",
      borderCollapse: "collapse"
    }}>
        <thead style={{
        background: "#FAFAFA"
      }}>
          <tr>
            <th style={th}>Date</th>
            <th style={th}>Pharmacy</th>
            <th style={th}>Recipient</th>
            <th style={th}>Subject</th>
            <th style={th}>Content</th>
            <th style={{
            ...th,
            textAlign: "right"
          }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {ordered.length === 0 ? <tr><td colSpan={6} style={{
            ...td,
            textAlign: "center",
            color: "#8C8C8C",
            padding: 20
          }}>No messages.</td></tr> : ordered.map(m => <tr key={m.id}>
              <td style={{
            ...td,
            whiteSpace: "nowrap"
          }}>{fmtDateTime(m.sentAt || m.createdAt)}</td>
              <td style={{
            ...td,
            fontWeight: 600
          }}>{displayName(m.pharmacyName, masked)}</td>
              <td style={td}>{m.recipientName || "—"}</td>
              <td style={td}>{m.subject || "—"}</td>
              <td style={td}>
                <div onMouseEnter={e => onHoverContent(m, e)} onMouseLeave={() => onHoverContent(null)} style={{
              maxWidth: 280,
              maxHeight: 44,
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: 12,
              color: "#4b5563",
              border: "1px solid #F0F0F0",
              borderRadius: 4,
              padding: "4px 6px",
              background: "#fafafa",
              cursor: "help",
              lineHeight: 1.4
            }}>
                  {m.body}
                </div>
              </td>
              <td style={{
            ...td,
            textAlign: "right",
            whiteSpace: "nowrap"
          }}>
                {(() => {
              const sc = typeof statusColor === "function" ? statusColor(m) : statusColor;
              return <span style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "3px 8px",
                borderRadius: 999,
                color: sc.text,
                background: sc.bg
              }}>
                      {typeof statusLabel === "function" ? statusLabel(m) : statusLabel}
                    </span>;
            })()}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}

/* ------------------------------------------------------------------ */
/* Content hover popup                                                */
/* ------------------------------------------------------------------ */
function ContentPopup({
  hover
}) {
  if (!hover) return null;
  const {
    msg,
    x,
    y
  } = hover;
  return <div style={{
    position: "fixed",
    left: Math.min(x + 14, window.innerWidth - 360),
    top: Math.min(y + 14, window.innerHeight - 200),
    width: 340,
    maxHeight: 260,
    overflow: "auto",
    zIndex: 2000,
    background: "#262626",
    color: "#FAFAFA",
    borderRadius: 8,
    padding: "12px 14px",
    boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
    fontSize: 13,
    lineHeight: 1.5,
    pointerEvents: "none"
  }}>
      <div style={{
      fontWeight: 700,
      marginBottom: 4
    }}>{msg.subject}</div>
      <div style={{
      whiteSpace: "pre-wrap"
    }}>{msg.body}</div>
    </div>;
}

/* ------------------------------------------------------------------ */
/* Preview dialog (for one pharmacy's selected messages)              */
/* ------------------------------------------------------------------ */
function PreviewDialog({
  pharmacy,
  recipientName,
  messages,
  requireAck,
  onClose
}) {
  return <div onClick={onClose} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1500
  }}>
      <div onClick={e => e.stopPropagation()} style={{
      width: "min(640px, 95vw)",
      maxHeight: "88vh",
      background: "#fff",
      borderRadius: 8,
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
    }}>
        <div style={{
        padding: "14px 18px",
        borderBottom: "1px solid #eee"
      }}>
          <div style={{
          fontWeight: 600,
          fontSize: 15
        }}>Preview — {pharmacy}</div>
          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginTop: 3
        }}>To: {recipientName || "branch team"}</div>
        </div>
        <div style={{
        padding: 20,
        overflow: "auto",
        flex: 1,
        fontSize: 14,
        color: "#434343",
        lineHeight: 1.6
      }}>
          {messages.length === 0 ? <p style={{
          fontStyle: "italic",
          color: "#5D5D5D"
        }}>No messages selected for this pharmacy.</p> : <>
              <p style={{
            marginTop: 0
          }}>Hello {recipientName || "team"},</p>
              <p>We wanted to share a few points with you to help keep everything on track at {pharmacy}:</p>
              <ul style={{
            paddingLeft: 22
          }}>
                {messages.map(m => <li key={m.id} style={{
              marginBottom: 10
            }}>
                    <strong>{m.subject}</strong>{m.body ? <>: {m.body}</> : null}
                  </li>)}
              </ul>
              {requireAck && <p style={{
            fontWeight: 600
          }}>
                  Please let us know when these items have been actioned by replying to this message.
                </p>}
              <p>If anything needs a hand, just let us know — we're here to help.</p>
              <p>With thanks,<br />The Pharmace team</p>
            </>}
        </div>
        <div style={{
        padding: "10px 18px",
        borderTop: "1px solid #eee",
        display: "flex",
        justifyContent: "flex-end"
      }}>
          <button onClick={onClose} style={{
          fontSize: 12,
          padding: "6px 16px",
          border: "1px solid #8C8C8C",
          borderRadius: 4,
          background: "#F5F5F5",
          cursor: "pointer"
        }}>Close</button>
        </div>
      </div>
    </div>;
}

/* ------------------------------------------------------------------ */
/* Confirm-send dialog — lists pharmacies with something ticked,      */
/* message visible on hover, Proceed / Cancel.                        */
/* ------------------------------------------------------------------ */
function ConfirmSendDialog({
  summary,
  masked,
  sending,
  onProceed,
  onCancel
}) {
  const [hover, setHover] = useState(null);
  const totalMessages = summary.reduce((n, s) => n + s.messages.length, 0);
  return <div onClick={onCancel} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1600
  }} onMouseMove={e => hover && setHover(h => h ? {
    ...h,
    x: e.clientX,
    y: e.clientY
  } : h)}>
      <div onClick={e => e.stopPropagation()} style={{
      width: "min(560px, 95vw)",
      maxHeight: "85vh",
      background: "#fff",
      borderRadius: 8,
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
    }}>
        <div style={{
        padding: "14px 18px",
        borderBottom: "1px solid #eee"
      }}>
          <div style={{
          fontWeight: 600,
          fontSize: 15
        }}>Confirm send</div>
          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginTop: 3
        }}>
            {totalMessages} message{totalMessages === 1 ? "" : "s"} to {summary.length} pharmac{summary.length === 1 ? "y" : "ies"}. Hover a message to read it.
          </div>
        </div>

        <div style={{
        padding: 14,
        overflow: "auto",
        flex: 1
      }}>
          {summary.map((s, i) => <div key={i} style={{
          marginBottom: 12
        }}>
              <div style={{
            fontWeight: 600,
            fontSize: 13,
            marginBottom: 4
          }}>
                {displayName(s.pharmacy, masked)}
                <span style={{
              fontWeight: 400,
              color: "#5D5D5D",
              marginLeft: 8
            }}>→ {s.recipientName}</span>
                {!s.pharmacyId && <span style={{
              color: "#CF1322",
              fontSize: 11,
              marginLeft: 8
            }}>(no id — cannot send)</span>}
              </div>
              <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6
          }}>
                {s.messages.map((m, j) => <span key={j} onMouseEnter={e => setHover({
              m,
              x: e.clientX,
              y: e.clientY
            })} onMouseLeave={() => setHover(null)} style={{
              fontSize: 12,
              padding: "3px 10px",
              borderRadius: 999,
              cursor: "help",
              background: m.requireAck ? "#FFF7E6" : "#eef2ff",
              color: m.requireAck ? "#AD4E00" : "#1D39C4",
              border: "1px solid " + (m.requireAck ? "#FFE58F" : "#c7d2fe")
            }}>
                    {m.subject || "(no subject)"}{m.requireAck ? " · ack" : ""}
                  </span>)}
              </div>
            </div>)}
        </div>

        <div style={{
        padding: "10px 18px",
        borderTop: "1px solid #eee",
        display: "flex",
        justifyContent: "flex-end",
        gap: 8
      }}>
          <button onClick={onCancel} disabled={sending} style={{
          fontSize: 12,
          padding: "6px 16px",
          border: "1px solid #8C8C8C",
          borderRadius: 4,
          background: "#F5F5F5",
          cursor: "pointer"
        }}>Cancel</button>
          <button onClick={onProceed} disabled={sending} style={{
          fontSize: 12,
          padding: "6px 18px",
          border: "1px solid #262626",
          borderRadius: 4,
          background: sending ? "#8C8C8C" : "#262626",
          color: "#fff",
          fontWeight: 600,
          cursor: sending ? "wait" : "pointer"
        }}>
            {sending ? "Sending…" : "Proceed"}
          </button>
        </div>
      </div>

      {hover && <div style={{
      position: "fixed",
      left: Math.min(hover.x + 14, window.innerWidth - 360),
      top: Math.min(hover.y + 14, window.innerHeight - 180),
      width: 340,
      maxHeight: 220,
      overflow: "auto",
      zIndex: 2100,
      background: "#262626",
      color: "#FAFAFA",
      borderRadius: 8,
      padding: "12px 14px",
      boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
      fontSize: 13,
      lineHeight: 1.5,
      pointerEvents: "none"
    }}>
          <div style={{
        fontWeight: 700,
        marginBottom: 4
      }}>{hover.m.subject || "(no subject)"}</div>
          <div style={{
        whiteSpace: "pre-wrap"
      }}>{hover.m.body || "(no body)"}</div>
        </div>}
    </div>;
}

/* ------------------------------------------------------------------ */
/* Compose dialog for "Add message to all pharmacies"                 */
/* ------------------------------------------------------------------ */
function AddToAllDialog({
  pharmacyCount,
  onCancel,
  onAdd
}) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const submit = () => {
    if (!subject.trim() && !body.trim()) {
      appAlert("The message is empty. Add a subject or body before adding it to all pharmacies.");
      return;
    }
    onAdd({
      subject: subject.trim() || "(no subject)",
      body: body.trim()
    });
  };
  return <div onClick={onCancel} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1600
  }}>
      <div onClick={e => e.stopPropagation()} style={{
      width: "min(560px, 95vw)",
      background: "#fff",
      borderRadius: 8,
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
    }}>
        <div style={{
        padding: "14px 18px",
        borderBottom: "1px solid #eee"
      }}>
          <div style={{
          fontWeight: 600,
          fontSize: 15
        }}>Add message to all pharmacies</div>
          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginTop: 3
        }}>
            This message will be added (ticked) to {pharmacyCount} pharmac{pharmacyCount === 1 ? "y" : "ies"}. You can still edit or untick it per pharmacy before sending.
          </div>
        </div>

        <div style={{
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 12
      }}>
          <div>
            <label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            marginBottom: 4
          }}>Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" style={{
            width: "100%",
            fontSize: 13,
            fontWeight: 600,
            border: "1px solid #D9D9D9",
            borderRadius: 4,
            padding: "6px 8px"
          }} />
          </div>
          <div>
            <label style={{
            display: "block",
            fontSize: 11,
            fontWeight: 600,
            marginBottom: 4
          }}>Message</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Message body" rows={5} style={{
            width: "100%",
            fontSize: 13,
            color: "#434343",
            border: "1px solid #D9D9D9",
            borderRadius: 4,
            padding: "6px 8px",
            resize: "vertical",
            fontFamily: "inherit"
          }} />
          </div>
        </div>

        <div style={{
        padding: "10px 18px",
        borderTop: "1px solid #eee",
        display: "flex",
        justifyContent: "flex-end",
        gap: 8
      }}>
          <button onClick={onCancel} style={{
          fontSize: 12,
          padding: "6px 16px",
          border: "1px solid #8C8C8C",
          borderRadius: 4,
          background: "#F5F5F5",
          cursor: "pointer"
        }}>Cancel</button>
          <button onClick={submit} style={{
          fontSize: 12,
          padding: "6px 18px",
          border: "1px solid #262626",
          borderRadius: 4,
          background: "#262626",
          color: "#fff",
          fontWeight: 600,
          cursor: "pointer"
        }}>Add message to all pharmacies</button>
        </div>
      </div>
    </div>;
}

/* ------------------------------------------------------------------ */
/* Main component                                                     */
/* ------------------------------------------------------------------ */
function ActionCentre() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const masked = useMaskPharmacyNames();
  const viewerOrg = localStorage.getItem("organisationName") || "";
  const isAdmin = viewerOrg === "Pharmace all";

  // Three message stores.
  const [awaiting, setAwaiting] = useState([]); // grid 1
  const [possible, setPossible] = useState({}); // grid 2: { [pharmacy]: { dbName, recipientStaffId, recipientName, recipientEmail, managers, messages:[...] } }
  const [acknowledged, setAcknowledged] = useState([]); // grid 3

  // UI state
  const [hover, setHover] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const [search, setSearch] = useState("");
  const [pharmacyFilter, setPharmacyFilter] = useState("");
  const [ackTotal, setAckTotal] = useState(0);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(null); // [{pharmacy, messages:[{subject,body,requireAck}]}] | null
  const [composeAll, setComposeAll] = useState(false); // "add message to all" compose dialog open

  // Message-topic switches (fleet-wide) — which compliance topics are
  // allowed to feed grid 2's suggestions at all. [{key,label,enabled}]
  const [topics, setTopics] = useState([]);
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [savingTopic, setSavingTopic] = useState(""); // key currently being saved, or ""

  const tokenRef = useRef(null);
  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${tokenRef.current}`
  });
  const orgQS = () => isAdmin || !viewerOrg ? "" : `&organisationName=${encodeURIComponent(viewerOrg)}`;
  useEffect(() => {
    tokenRef.current = localStorage.getItem("authToken");
    loadSeed();
    loadAwaiting();
    loadAcknowledged();
    loadTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Message-topic switches: which compliance topics feed grid 2 ----
  const loadTopics = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/topic-settings`, {
        headers: {
          Authorization: `Bearer ${tokenRef.current}`
        }
      });
      if (!res.ok) return;
      const json = await res.json();
      setTopics(Array.isArray(json.topics) ? json.topics : []);
    } catch (_) {/* leave as-is */}
  };

  // Flip one topic on/off, save immediately (same "saved automatically"
  // pattern as the recipient dropdown), then re-seed grid 2 so a switched-off
  // topic's suggestions disappear right away rather than on next page load.
  const toggleTopic = async key => {
    const next = topics.map(t => t.key === key ? {
      ...t,
      enabled: !t.enabled
    } : t);
    setTopics(next);
    setSavingTopic(key);
    try {
      const excludedModules = next.filter(t => !t.enabled).map(t => t.key);
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/topic-settings`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          excludedModules
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setTopics(Array.isArray(json.topics) ? json.topics : next);
      await loadSeed(); // re-fetch suggestions so grid 2 reflects the change now
    } catch (e) {
      setTopics(topics); // revert the optimistic flip
      (await appAlert("Could not save topic setting: " + (e.message || e)));
    } finally {
      setSavingTopic("");
    }
  };

  // ---- Load grid 1 (outstanding: requires ack, not yet acknowledged) ----
  const loadAwaiting = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/messages?status=outstanding&limit=500${orgQS()}`, {
        headers: {
          Authorization: `Bearer ${tokenRef.current}`
        }
      });
      if (!res.ok) return;
      const json = await res.json();
      setAwaiting((json.rows || []).map(normaliseRow));
    } catch (_) {/* leave as-is */}
  };

  // ---- Load grid 3 (done: no ack needed, or already acknowledged) ----
  const loadAcknowledged = async (reset = true) => {
    try {
      const skip = reset ? 0 : acknowledged.length;
      const params = `status=done&limit=${PAGE_SIZE}&skip=${skip}` + (search.trim() ? `&search=${encodeURIComponent(search.trim())}` : "") + (pharmacyFilter ? `&pharmacyId=${encodeURIComponent(pharmacyFilter)}` : "") + orgQS();
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/messages?${params}`, {
        headers: {
          Authorization: `Bearer ${tokenRef.current}`
        }
      });
      if (!res.ok) return;
      const json = await res.json();
      const rows = (json.rows || []).map(normaliseRow);
      setAckTotal(json.total || 0);
      setAcknowledged(prev => reset ? rows : [...prev, ...rows]);
    } catch (_) {/* leave as-is */}
  };

  // Normalise a backend message doc to the shape the grids render.
  const normaliseRow = d => ({
    id: String(d._id),
    pharmacyId: d.pharmacyId ? String(d.pharmacyId) : null,
    pharmacyName: d.pharmacyName,
    recipientName: d.recipientName,
    subject: d.subject,
    body: d.body,
    bullets: Array.isArray(d.bullets) ? d.bullets : [],
    sentAt: d.sentAt,
    createdAt: d.sentAt,
    requireAck: !!d.requireAck,
    opened: !!d.opened,
    openedAt: d.openedAt,
    acknowledged: !!d.acknowledged,
    acknowledgedAt: d.acknowledgedAt
  });

  // Seed grid 2 from organisation members, then merge real near-miss/compliance suggestions.
  const loadSeed = async () => {
    setLoading(true);
    setError("");
    try {
      const token = tokenRef.current;
      let alerts = [];
      let pharmacyIdByName = {};
      let dbNameByName = {};
      let managersByDbName = {};
      let savedRecipientByDbName = {};
      const memberIndex = organisationMemberIndex();
      // Resolve any upstream row to the canonical organisation member. For
      // ordinary organisation users, an unrecognised branch is rejected rather
      // than creating a phantom Action Centre card.
      const canonicalMember = ({
        dbName,
        pharmacyName
      } = {}) => {
        if (isAdmin) return null;
        const dbKey = String(dbName || "").trim();
        if (dbKey && memberIndex.byDbName.has(dbKey)) return memberIndex.byDbName.get(dbKey);
        return memberIndex.byName.get(normBranch(pharmacyName)) || null;
      };
      if (token) {
        try {
          const res = await fetch(`${API_BASE}/near_miss_alerts_fleet`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              status: "pending"
            })
          });
          if (res.ok) {
            const json = await res.json();
            alerts = Array.isArray(json.alerts) ? json.alerts : [];
          }
        } catch (_) {/* near-miss feed is best-effort */}
      }

      // Org scope.
      if (!isAdmin) alerts = alerts.filter(a => (a.organisationName || "") === viewerOrg);

      // Pre-seed exactly the organisation's branches. This means a branch stays
      // messageable even with zero current suggestions, while stale/foreign
      // branch names from alert feeds cannot create extra cards.
      const byPharm = {};
      if (!isAdmin) {
        for (const m of memberIndex.list) {
          byPharm[m.name] = [];
          if (m.dbName) dbNameByName[m.name] = m.dbName;
        }
      }

      // Group alert-derived suggestions by canonical pharmacy.
      for (const a of alerts) {
        const member = canonicalMember({
          dbName: a.dbName,
          pharmacyName: a.pharmacyName
        });
        if (!isAdmin && !member) continue;
        const name = member ? member.name : a.pharmacyName || "(unknown branch)";
        if (!byPharm[name]) byPharm[name] = [];
        byPharm[name].push({
          id: nextId(),
          alertId: a._id ? String(a._id) : null,
          // source near_miss_alerts doc
          kind: "nearmiss",
          subject: a.insightTitle || "Near-miss insight",
          body: a.summary || a.fullDetail || a.insightTitle || "A near-miss pattern was flagged for review.",
          include: false,
          requireAck: false,
          seed: true
        });
        if (a.pharmacyId) pharmacyIdByName[name] = String(a.pharmacyId);
        if (a.dbName) dbNameByName[name] = String(a.dbName);
      }

      // Fetch real RED compliance items per pharmacy and merge them in as
      // additional (unticked) suggestion lines.
      if (token) {
        try {
          const sres = await fetch(`${API_BASE}/api/headoffice/action-centre/suggestions?${orgQS().replace(/^&/, "")}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          if (sres.ok) {
            const groups = await sres.json();
            for (const g of Array.isArray(groups) ? groups : []) {
              const member = canonicalMember({
                dbName: g.dbName,
                pharmacyName: g.pharmacyName
              });
              if (!isAdmin && !member) continue;
              const name = member ? member.name : g.pharmacyName || "(unknown branch)";
              if (!byPharm[name]) byPharm[name] = [];
              if (g.pharmacyId) pharmacyIdByName[name] = String(g.pharmacyId);
              if (g.dbName) dbNameByName[name] = String(g.dbName);
              for (const line of g.lines || []) {
                byPharm[name].push({
                  id: nextId(),
                  alertId: null,
                  kind: "summary",
                  moduleKey: line.moduleKey,
                  subject: line.label,
                  body: line.detail,
                  include: false,
                  requireAck: false,
                  seed: true
                });
              }
            }
          }
        } catch (_) {/* compliance suggestions are best-effort */}
      }

      // Recipient choices use the same live staff source as Communications -> Messages.
      // Only active Manager / Branch Manager / Pharmacist staff with a valid
      // email are eligible.
      if (token) {
        try {
          const [staffRes, recipientRes] = await Promise.all([fetch(`${API_BASE}/api/headoffice/staff-messages/staff`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({})
          }), fetch(`${API_BASE}/api/headoffice/action-centre/recipients`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          })]);
          if (staffRes.ok) {
            const staffJson = await staffRes.json();
            for (const person of Array.isArray(staffJson.staff) ? staffJson.staff : []) {
              const role = String(person.roleName || "").trim().toLowerCase();
              // Manager and pharmacist roles vary between tenants (e.g. "Group
              // Manager", "Pharmacy Manager", "Branch Manager", "Pharmacist",
              // "Locum Pharmacist", "Responsible Pharmacist"). Treat any active
              // role containing "manager" or "pharmacist" as an eligible Action
              // Centre recipient rather than requiring exact role-name strings.
              const eligibleRole = role.includes("manager") || role.includes("pharmacist");
              if (person.active === false || !person.hasEmail || !eligibleRole) continue;
              const dbName = String(person.dbName || "");
              if (!dbName) continue;
              if (!managersByDbName[dbName]) managersByDbName[dbName] = [];
              managersByDbName[dbName].push(person);
            }
          }
          if (recipientRes.ok) {
            const recipientJson = await recipientRes.json();
            for (const saved of Array.isArray(recipientJson) ? recipientJson : []) {
              if (saved.dbName) savedRecipientByDbName[String(saved.dbName)] = saved;
            }
          }
        } catch (_) {/* recipient list is best-effort; blank means sending is disabled */}
      }

      // Never manufacture pharmacies in production. If an admin fleet view has
      // no live rows it is genuinely empty; organisation views were already
      // pre-seeded from members[] above.

      const possibleInit = {};
      for (const [name, messages] of Object.entries(byPharm)) {
        const dbName = dbNameByName[name] || null;
        const managers = dbName ? managersByDbName[dbName] || [] : [];
        const saved = dbName ? savedRecipientByDbName[dbName] : null;
        // Restore only a still-eligible saved staff member. If they have left,
        // changed role or lost their email, deliberately fall back to BLANK.
        const selected = saved ? managers.find(m => String(m.staffId) === String(saved.staffId)) : null;
        possibleInit[name] = {
          pharmacyId: pharmacyIdByName[name] || null,
          dbName,
          recipientStaffId: selected ? String(selected.staffId) : "",
          recipientName: selected ? selected.name : "",
          recipientEmail: selected ? selected.email || "" : "",
          managers,
          requireAck: false,
          messages
        };
      }
      setPossible(possibleInit);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  /* ---------- grid 2 mutations ---------- */
  const updatePossible = (pharmacy, updater) => {
    setPossible(prev => {
      const cur = prev[pharmacy] || {
        pharmacyId: null,
        dbName: null,
        recipientStaffId: "",
        recipientName: "",
        recipientEmail: "",
        managers: [],
        requireAck: false,
        messages: []
      };
      const copy = {
        ...cur,
        managers: [...(cur.managers || [])],
        messages: cur.messages.map(m => ({
          ...m
        }))
      };
      updater(copy);
      return {
        ...prev,
        [pharmacy]: copy
      };
    });
  };

  // Save the chosen Action Centre recipient immediately. Blank deliberately
  // clears the saved choice; there is no email or organisation fallback.
  const saveRecipient = async (pharmacy, staffId) => {
    const before = possible[pharmacy];
    if (!before || !before.pharmacyId) return;
    const selected = (before.managers || []).find(s => String(s.staffId) === String(staffId));
    updatePossible(pharmacy, st => {
      st.recipientStaffId = selected ? String(selected.staffId) : "";
      st.recipientName = selected ? selected.name : "";
      st.recipientEmail = selected ? selected.email || "" : "";
    });
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/recipient`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          pharmacyId: before.pharmacyId,
          staffId: selected ? String(selected.staffId) : null
        })
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
    } catch (e) {
      // Put the UI back exactly as it was if persistence/validation fails.
      setPossible(prev => ({
        ...prev,
        [pharmacy]: before
      }));
      (await appAlert(`Recipient choice was not saved: ${e.message || e}`));
    }
  };
  const toggleBlockAck = pharmacy => {
    updatePossible(pharmacy, st => {
      st.requireAck = !st.requireAck;
    });
  };
  const toggleMsg = (pharmacy, id, field) => {
    updatePossible(pharmacy, st => {
      const m = st.messages.find(x => x.id === id);
      if (m) m[field] = !m[field];
    });
  };
  const editMsg = (pharmacy, id, field, value) => {
    updatePossible(pharmacy, st => {
      const m = st.messages.find(x => x.id === id);
      if (m) m[field] = value;
    });
  };
  const addMessageToPharmacy = pharmacy => {
    updatePossible(pharmacy, st => {
      st.messages.push({
        id: nextId(),
        subject: "New message",
        body: "",
        include: true,
        requireAck: false,
        seed: false
      });
    });
  };

  // Dismiss a near-miss-derived suggestion: mark it dismissed in the
  // near_miss_alerts collection so the nightly engine stops re-raising it,
  // strike it out locally, then drop it on the next load. Non-alert (manually
  // added) rows have no alertId — just remove them locally.
  const dismissAlert = async (pharmacy, id) => {
    let alertId = null;
    setPossible(prev => {
      const block = prev[pharmacy];
      const m = block?.messages.find(x => x.id === id);
      alertId = m?.alertId || null;
      return prev;
    });
    if (!alertId) {
      // Locally-added row — just remove it.
      updatePossible(pharmacy, st => {
        st.messages = st.messages.filter(x => x.id !== id);
      });
      return;
    }

    // Optimistically strike it out.
    updatePossible(pharmacy, st => {
      const m = st.messages.find(x => x.id === id);
      if (m) {
        m.dismissed = true;
        m.include = false;
      }
    });
    try {
      const res = await fetch(`${API_BASE}/near_miss_alerts_dismiss`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          alertId
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Leave it struck-out for this session; it won't return on next load.
    } catch (e) {
      // Roll back the strike-out on failure.
      updatePossible(pharmacy, st => {
        const m = st.messages.find(x => x.id === id);
        if (m) m.dismissed = false;
      });
      (await appAlert("Dismiss failed: " + (e.message || e)));
    }
  };

  // Add a composed message line (auto-ticked) to every pharmacy's grid.
  const addMessageToAll = ({
    subject,
    body
  }) => {
    setPossible(prev => {
      const next = {};
      for (const [name, block] of Object.entries(prev)) {
        next[name] = {
          ...block,
          messages: [...block.messages.map(m => ({
            ...m
          })), {
            id: nextId(),
            subject,
            body,
            include: true,
            seed: false
          }]
        };
      }
      return next;
    });
  };

  /* ---------- sending ---------- */
  // Helper: assemble one pharmacy's ticked lines into a single message.
  // The body mirrors the Preview dialog's letter format exactly.
  const buildMessageFor = (pharmacy, block) => {
    const ticked = block.messages.filter(m => m.include && !m.dismissed);
    if (!ticked.length) return null;
    const bullets = ticked.map(m => ({
      subject: m.subject,
      body: m.body,
      alertId: m.alertId || null
    }));
    const requireAck = !!block.requireAck;
    const recipientName = block.recipientName || "team";
    const displayPharmacy = displayName(pharmacy, masked);

    // Subject: single line's subject, or a summary for multiple.
    const subject = ticked.length === 1 ? ticked[0].subject || "Message" : `${ticked.length} items for your attention`;

    // Body: same supportive letter as the Preview dialog.
    const lines = [];
    lines.push(`Hello ${recipientName},`);
    lines.push("");
    lines.push(`We wanted to share a few points with you to help keep everything on track at ${displayPharmacy}:`);
    lines.push("");
    for (const m of ticked) {
      lines.push(`• ${m.subject}${m.body ? `: ${m.body}` : ""}`);
    }
    lines.push("");
    if (requireAck) {
      lines.push("Please let us know when these items have been actioned by replying to this message.");
      lines.push("");
    }
    lines.push("If anything needs a hand, just let us know — we're here to help.");
    lines.push("");
    lines.push("With thanks,");
    lines.push("The Pharmace team");
    const body = lines.join("\n");
    return {
      pharmacyId: block.pharmacyId,
      pharmacyName: pharmacy,
      recipientStaffId: block.recipientStaffId || "",
      recipientName: block.recipientName || "",
      requireAck,
      subject,
      body,
      bullets,
      alertIds: ticked.map(m => m.alertId).filter(Boolean),
      localIds: ticked.map(m => m.id)
    };
  };

  // Build the per-pharmacy combined messages, then open the confirm dialog.
  const requestSend = (onlyPharmacy = null) => {
    const summary = [];
    const missingRecipients = [];
    for (const [pharmacy, block] of Object.entries(possible)) {
      if (onlyPharmacy && pharmacy !== onlyPharmacy) continue;
      const hasTicked = block.messages.some(m => m.include && !m.dismissed);
      if (hasTicked && !block.recipientStaffId) {
        missingRecipients.push(pharmacy);
        continue;
      }
      const msg = buildMessageFor(pharmacy, block);
      if (msg) {
        summary.push({
          pharmacy,
          pharmacyId: msg.pharmacyId,
          recipientName: msg.recipientName,
          requireAck: msg.requireAck,
          // confirm dialog shows the bullet items as the "messages" list
          messages: msg.bullets.map(b => ({
            subject: b.subject,
            body: b.body,
            requireAck: msg.requireAck
          }))
        });
      }
    }
    if (missingRecipients.length) {
      appAlert(`No Action Centre recipient is selected for: ${missingRecipients.join(", ")}. Select a manager or pharmacist before sending.`);
      return;
    }
    if (summary.length === 0) {
      appAlert(onlyPharmacy ? "Nothing is ticked for this pharmacy. Tick the messages you'd like to include first." : "No messages are ticked to send. Tick the messages you'd like to include first.");
      return;
    }
    setConfirmSend(summary);
  };
  const sendAll = async () => {
    // The set of pharmacies to send = whatever is in the confirm dialog
    // (all pharmacies for "send to all", or one for "send to this pharmacy").
    const scope = new Set((confirmSend || []).map(s => s.pharmacy));
    if (scope.size === 0) {
      setConfirmSend(null);
      return;
    }

    // One combined message per pharmacy in scope.
    const payload = [];
    const sentLocalIds = []; // {pharmacy, id}
    const sentAlertIds = [];
    for (const [pharmacy, block] of Object.entries(possible)) {
      if (!scope.has(pharmacy)) continue;
      const msg = buildMessageFor(pharmacy, block);
      if (!msg) continue;
      payload.push({
        pharmacyId: msg.pharmacyId,
        pharmacyName: msg.pharmacyName,
        recipientStaffId: msg.recipientStaffId,
        subject: msg.subject,
        body: msg.body,
        bullets: msg.bullets,
        requireAck: msg.requireAck
      });
      msg.localIds.forEach(id => sentLocalIds.push({
        pharmacy,
        id
      }));
      msg.alertIds.forEach(id => sentAlertIds.push(id));
    }
    if (payload.length === 0) {
      setConfirmSend(null);
      return;
    }
    if (payload.some(p => !p.pharmacyId)) {
      (await appAlert("Some selected pharmacies have no pharmacyId resolved, so they can't be sent yet. (These are mock/seed branches without a backend id.)"));
      setConfirmSend(null);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/messages/send`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          messages: payload
        })
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);

      // Mark the source near-miss alerts 'sent' (no-email endpoint).
      if (sentAlertIds.length) {
        await Promise.all(sentAlertIds.map(alertId => fetch(`${API_BASE}/near_miss_alerts_mark_sent`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            alertId
          })
        }).catch(() => null)));
      }

      // Strip sent lines from grid 2, but KEEP the pharmacy block even when
      // it's left empty — so it stays visible ("No current recommendations")
      // and the manager can still message it.
      setPossible(prev => {
        const next = {};
        for (const [pharmacy, block] of Object.entries(prev)) {
          const sentIds = new Set(sentLocalIds.filter(s => s.pharmacy === pharmacy).map(s => s.id));
          const keep = block.messages.filter(m => !sentIds.has(m.id));
          next[pharmacy] = {
            ...block,
            messages: keep
          };
        }
        return next;
      });

      // Refresh grids 1 and 3 from the server.
      await Promise.all([loadAwaiting(), loadAcknowledged(true)]);
    } catch (e) {
      (await appAlert("Send failed: " + (e.message || e)));
    } finally {
      setSending(false);
      setConfirmSend(null);
    }
  };

  // Manual override for a grid-1 row: the recipient normally acknowledges
  // themselves from the button in their email (no login needed — see the
  // backend's public /action-centre/ack/:token routes), which is what moves
  // a message to grid 3. This is the fallback for e.g. "they confirmed by
  // phone" — same authenticated endpoint, just triggered from the admin UI.
  const markAcknowledged = async row => {
    if (!(await appConfirm(`Mark this message to ${row.recipientName || "the recipient"} as acknowledged?`))) return;
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/action-centre/messages/${row.id}/acknowledge`, {
        method: "POST",
        headers: authHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await Promise.all([loadAwaiting(), loadAcknowledged(true)]);
    } catch (e) {
      (await appAlert("Update failed: " + (e.message || e)));
    }
  };

  /* ---------- grid 3 (server-driven) ---------- */
  // Re-query grid 3 when search / pharmacy filter change (debounced).
  useEffect(() => {
    const t = setTimeout(() => loadAcknowledged(true), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pharmacyFilter]);
  const pharmacyOptions = useMemo(() => {
    const byId = new Map();
    for (const [name, block] of Object.entries(possible)) {
      if (block.pharmacyId) byId.set(String(block.pharmacyId), name);
    }
    for (const m of [...awaiting, ...acknowledged]) {
      if (m.pharmacyId && !byId.has(m.pharmacyId)) byId.set(m.pharmacyId, m.pharmacyName);
    }
    return [...byId.entries()].map(([id, name]) => ({
      id,
      name
    })).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [possible, awaiting, acknowledged]);
  const onHoverContent = (msg, e) => {
    if (!msg) {
      setHover(null);
      return;
    }
    setHover({
      msg,
      x: e.clientX,
      y: e.clientY
    });
  };
  const possibleEntries = Object.entries(possible).sort((a, b) => a[0].localeCompare(b[0]));
  return <div style={{
    padding: "16px 20px"
  }} onMouseMove={e => hover && setHover(h => h ? {
    ...h,
    x: e.clientX,
    y: e.clientY
  } : h)}>
      {/* Header */}
      <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
      gap: 12,
      flexWrap: "wrap"
    }}>
        <div>
          <h2 style={{
          margin: 0,
          fontSize: 20
        }}>Action Centre</h2>
          <div style={{
          fontSize: 13,
          color: "#5D5D5D",
          marginTop: 4
        }}>
            {isAdmin ? "Across the estate" : viewerOrg}
          </div>
        </div>
        <div style={{
        display: "flex",
        gap: 8,
        alignItems: "center"
      }}>
          <MaskToggleButton />
          <button onClick={() => setComposeAll(true)} style={btnStyle()}>Add message to all pharmacies</button>
          <button onClick={requestSend} disabled={sending} style={btnStyle(true)}>
            {sending ? "Sending…" : "Send message to all pharmacies"}
          </button>
        </div>
      </div>

      {error && <div style={{
      color: "#CF1322",
      padding: 12,
      background: "#FFF1F0",
      borderRadius: 4,
      marginBottom: 12
    }}>Error: {error}</div>}
      {loading && <div style={{
      color: "#5D5D5D",
      padding: 24
    }}>Loading…</div>}

      {!loading && <>
          {/* GRID 1: awaiting acknowledgement */}
          <h3 style={{
        fontSize: 15,
        margin: "18px 0 8px"
      }}>
            Awaiting acknowledgement <span style={{
          fontWeight: 400,
          color: "#5D5D5D"
        }}>({awaiting.length})</span>
          </h3>
          <MessageGrid rows={awaiting} oldestFirst masked={masked} statusLabel={m => <span onClick={() => markAcknowledged(m)} style={{
        cursor: "pointer"
      }} title="The recipient normally acknowledges from the button in their email. Click here to mark it acknowledged manually instead (e.g. they confirmed by phone).">
                Mark acknowledged
              </span>} statusColor={{
        text: "#820014",
        bg: "#FFF1F0"
      }} onHoverContent={onHoverContent} />

          {/* GRID 2: possible messages to send */}
          <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        margin: "26px 0 8px",
        flexWrap: "wrap",
        gap: 8
      }}>
            <h3 style={{
          fontSize: 15,
          margin: 0
        }}>
              Current possible messages to send <span style={{
            fontWeight: 400,
            color: "#5D5D5D"
          }}>({possibleEntries.length} {possibleEntries.length === 1 ? "pharmacy" : "pharmacies"})</span>
            </h3>
            <button onClick={() => setTopicsOpen(o => !o)} style={btnStyle()}>
              {topicsOpen ? "Hide topics" : "Configure topics"}
            </button>
          </div>
          {topicsOpen && <div style={{
        marginBottom: 14,
        padding: "12px 14px",
        border: "1px solid #F0F0F0",
        borderRadius: 8,
        background: "#fff"
      }}>
              <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 8
        }}>
                Untick a topic to stop it ever being suggested, fleet-wide, until it's ticked again. Already-sent messages aren't affected.
              </div>
              <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px 18px"
        }}>
                {topics.length === 0 ? <span style={{
            fontSize: 12,
            color: "#8C8C8C"
          }}>Loading…</span> : topics.map(t => <label key={t.key} style={{
            fontSize: 13,
            color: "#434343",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            cursor: savingTopic ? "wait" : "pointer"
          }}>
                    <input type="checkbox" checked={t.enabled} disabled={!!savingTopic} onChange={() => toggleTopic(t.key)} />
                    {t.label}
                  </label>)}
              </div>
            </div>}
          {possibleEntries.length === 0 ? <div style={{
        color: "#5D5D5D",
        padding: 16,
        border: "1px solid #F0F0F0",
        borderRadius: 8,
        background: "#fff"
      }}>
              Nothing to suggest right now.
            </div> : possibleEntries.map(([pharmacy, block]) => <div key={pharmacy} style={{
        marginBottom: 14,
        border: "1px solid #F0F0F0",
        borderRadius: 8,
        overflow: "hidden",
        background: "#fff"
      }}>
              <div style={{
          padding: "10px 14px",
          borderBottom: "1px solid #eee",
          background: "#FAFAFA",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12
        }}>
                <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0
          }}>
                  <span style={{
              fontWeight: 600,
              whiteSpace: "nowrap"
            }}>{displayName(pharmacy, masked)}</span>
                  <span style={{
              fontSize: 12,
              color: "#5D5D5D"
            }}>→</span>
                  <select value={block.recipientStaffId || ""} onChange={e => saveRecipient(pharmacy, e.target.value)} disabled={!block.pharmacyId || !(block.managers || []).length} title="Action Centre recipient — saved automatically" style={{
              maxWidth: 360,
              fontSize: 12,
              border: "1px solid #D9D9D9",
              borderRadius: 4,
              padding: "4px 6px",
              background: "#fff"
            }}>
                    <option value="">{(block.managers || []).length ? "— Select recipient —" : "— No Manager / Pharmacist with email —"}</option>
                    {(block.managers || []).map(person => <option key={person.staffId} value={person.staffId}>
                        {person.name}{person.roleName ? ` (${person.roleName})` : ""} — {person.email}
                      </option>)}
                  </select>
                </div>
                <div style={{
            display: "flex",
            gap: 12,
            alignItems: "center"
          }}>
                  <label style={{
              fontSize: 12,
              color: "#434343",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer"
            }} title="If ticked, this pharmacy's message must be acknowledged by the recipient.">
                    <input type="checkbox" checked={!!block.requireAck} onChange={() => toggleBlockAck(pharmacy)} />
                    Require acknowledgement
                  </label>
                  <button onClick={() => addMessageToPharmacy(pharmacy)} style={btnStyle()}>+ Add another message</button>
                  <button onClick={() => setPreviewFor(pharmacy)} style={btnStyle()}>Preview</button>
                  <button onClick={() => requestSend(pharmacy)} disabled={sending || !block.recipientStaffId} title={!block.recipientStaffId ? "Select a recipient before sending" : ""} style={btnStyle(true)}>Send message to this pharmacy only</button>
                </div>
              </div>

              {/* Column headers (only when there are messages) */}
              {block.messages.length > 0 && <div style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "6px 14px",
          borderBottom: "1px solid #F5F5F5",
          fontSize: 10,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          color: "#8C8C8C"
        }}>
                  <div style={{
            width: 60,
            textAlign: "center"
          }}>Include</div>
                  <div style={{
            flex: 1
          }}>Message</div>
                  <div style={{
            width: 80,
            textAlign: "center"
          }}>Dismiss</div>
                </div>}

              {block.messages.length === 0 && <div style={{
          padding: "14px",
          fontSize: 13,
          color: "#8C8C8C",
          fontStyle: "italic"
        }}>
                  No current recommendations. You can still add a message below and send it.
                </div>}

              {block.messages.map(m => <div key={m.id} style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "9px 14px",
          borderBottom: "1px solid #F5F5F5",
          borderLeft: `3px solid ${m.kind === "summary" ? "#FA8C16" : m.kind === "nearmiss" ? "#F5222D" : "#8C8C8C"}`,
          opacity: m.dismissed ? 0.5 : 1
        }}>
                  <div style={{
            width: 60,
            textAlign: "center",
            paddingTop: 6
          }}>
                    <input type="checkbox" checked={m.include} disabled={m.dismissed} onChange={() => toggleMsg(pharmacy, m.id, "include")} />
                  </div>
                  <div style={{
            flex: 1,
            minWidth: 0,
            textDecoration: m.dismissed ? "line-through" : "none"
          }}>
                    {(m.kind === "nearmiss" || m.kind === "summary") && <span style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.4,
              color: "#8C8C8C",
              textTransform: "uppercase",
              display: "block",
              marginBottom: 2
            }}>
                        {m.kind === "nearmiss" ? "Near miss" : "Summary"}
                      </span>}
                    <input value={m.subject} onChange={e => editMsg(pharmacy, m.id, "subject", e.target.value)} placeholder="Subject" disabled={m.dismissed} style={{
              width: "100%",
              fontSize: 13,
              fontWeight: 600,
              border: "1px solid #F0F0F0",
              borderRadius: 4,
              padding: "4px 6px",
              marginBottom: 4,
              textDecoration: m.dismissed ? "line-through" : "none"
            }} />
                    <textarea value={m.body} onChange={e => editMsg(pharmacy, m.id, "body", e.target.value)} placeholder="Message body" rows={2} disabled={m.dismissed} style={{
              width: "100%",
              fontSize: 12,
              color: "#4b5563",
              border: "1px solid #F0F0F0",
              borderRadius: 4,
              padding: "4px 6px",
              resize: "vertical",
              fontFamily: "inherit"
            }} />
                  </div>
                  <div style={{
            width: 80,
            textAlign: "center",
            paddingTop: 4
          }}>
                    {m.dismissed ? <span style={{
              fontSize: 11,
              color: "#8C8C8C"
            }}>Dismissed</span> : <button onClick={() => dismissAlert(pharmacy, m.id)} title="Dismiss this suggestion so it won't be raised again" style={{
              fontSize: 11,
              padding: "4px 10px",
              border: "1px solid #CF1322",
              borderRadius: 4,
              background: "#fff",
              color: "#CF1322",
              cursor: "pointer"
            }}>
                        Dismiss
                      </button>}
                  </div>
                </div>)}
            </div>)}

          {/* GRID 3: acknowledged / no-ack */}
          <h3 style={{
        fontSize: 15,
        margin: "26px 0 8px"
      }}>
            Acknowledged / no acknowledgement required <span style={{
          fontWeight: 400,
          color: "#5D5D5D"
        }}>({ackTotal})</span>
          </h3>
          <div style={{
        display: "flex",
        gap: 8,
        marginBottom: 8,
        flexWrap: "wrap"
      }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search messages…" style={{
          flex: "1 1 240px",
          fontSize: 13,
          border: "1px solid #D9D9D9",
          borderRadius: 4,
          padding: "6px 10px"
        }} />
            <select value={pharmacyFilter} onChange={e => setPharmacyFilter(e.target.value)} style={{
          fontSize: 13,
          border: "1px solid #D9D9D9",
          borderRadius: 4,
          padding: "6px 10px",
          minWidth: 200
        }}>
              <option value="">All pharmacies</option>
              {pharmacyOptions.map(p => <option key={p.id} value={p.id}>{displayName(p.name, masked)}</option>)}
            </select>
          </div>
          <MessageGrid rows={acknowledged} oldestFirst={false} masked={masked} statusLabel={m => m.acknowledgedAt ? "Acknowledged" : "No acknowledgement required"} statusColor={{
        text: "#389E0D",
        bg: "#F6FFED"
      }} onHoverContent={onHoverContent} />
          {acknowledged.length < ackTotal && <div style={{
        textAlign: "center",
        marginTop: 10
      }}>
              <button onClick={() => loadAcknowledged(false)} style={btnStyle()}>
                Load more ({ackTotal - acknowledged.length} remaining)
              </button>
            </div>}
        </>}

      <ContentPopup hover={hover} />
      {previewFor && possible[previewFor] && <PreviewDialog pharmacy={displayName(previewFor, masked)} recipientName={possible[previewFor].recipientName} messages={possible[previewFor].messages.filter(m => m.include && !m.dismissed)} requireAck={!!possible[previewFor].requireAck} onClose={() => setPreviewFor(null)} />}
      {confirmSend && <ConfirmSendDialog summary={confirmSend} masked={masked} sending={sending} onProceed={sendAll} onCancel={() => setConfirmSend(null)} />}
      {composeAll && <AddToAllDialog pharmacyCount={Object.keys(possible).length} onCancel={() => setComposeAll(false)} onAdd={msg => {
      addMessageToAll(msg);
      setComposeAll(false);
    }} />}
    </div>;
}
function btnStyle(primary) {
  return {
    fontSize: 12,
    padding: "6px 12px",
    borderRadius: 4,
    cursor: "pointer",
    border: `1px solid ${primary ? "#262626" : "#8C8C8C"}`,
    background: primary ? "#262626" : "#fff",
    color: primary ? "#fff" : "#434343",
    fontWeight: primary ? 600 : 400,
    whiteSpace: "nowrap"
  };
}
export default ActionCentre;
export { ActionCentre };
